import * as React from 'react';
import { AutoSizer, Table as VTable, SortDirection, Index, SortDirectionType } from 'react-virtualized';
import { Dimensions } from 'react-virtualized/dist/es/AutoSizer';

import './Table.css';

type CellGetter<T> = (item: T, column: string | null) => number | string;

export type Props<T> = {
    items: ReadonlyArray<T>;
    children: React.ReactElement<any>[]; // tslint:disable-line:no-any
    noRowsRenderer: () => void;
    onClickRowItem?: (item: T) => void;
    onDoubleClickRowItem?: (item: T) => void;
    rowStyle?: {} | Function;
    cellGetter?: CellGetter<T>;
    initialSortBy?: string | null;
    initialSortDirection?: SortDirectionType;
};

type State<T> = {
    headerHeight: number;
    overscanRowCount: number;
    rowHeight: number;
    scrollToIndex?: number;
    sortBy: string | null;
    sortDirection: SortDirectionType;
    sortedItems: ReadonlyArray<T>;
};

const defaultCellGetter = <T extends number | string>(item: T, column: string | null) => item;

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

export class Table<T> extends React.Component<Props<T>, State<T>> {
    private static sortItems<T>(props: Props<T>, sortBy: string | null, sortDirection: SortDirectionType): ReadonlyArray<T> {
        const { items, cellGetter = defaultCellGetter as CellGetter<T> } = props;

        const sortedItems = [ ...items ].sort((a: T, b: T) => {
            const aCell = cellGetter(a, sortBy);
            const bCell = cellGetter(b, sortBy);
            if (typeof aCell === 'string' && typeof bCell === 'string') {
                return collator.compare(aCell, bCell);
            }
            return aCell < bCell ? -1 : aCell > bCell ? 1 : 0;
        });

        if (sortDirection === SortDirection.DESC) {
            sortedItems.reverse();
        }
        return sortedItems;
    }

    constructor(props: Props<T>) {
        super(props);

        const sortBy = props.initialSortBy || null;
        const sortDirection = props.initialSortDirection || SortDirection.ASC;

        this.state = {
            headerHeight: 25,
            overscanRowCount: 10,
            rowHeight: 25,
            scrollToIndex: undefined,
            sortBy,
            sortDirection,
            sortedItems: Table.sortItems<T>(props, sortBy, sortDirection),
        };
    }

    onRowClick = ({ index }: Index) => {
        const { onClickRowItem } = this.props;
        if (onClickRowItem) {
            onClickRowItem(this.state.sortedItems[index]);
        }
    }

    onRowDoubleClick = ({ index }: Index) => {
        const { onDoubleClickRowItem } = this.props;
        if (onDoubleClickRowItem) {
            onDoubleClickRowItem(this.state.sortedItems[index]);
        }
    }

    componentDidUpdate(oldProps: Props<T>) {
        if (this.props.items !== oldProps.items) {
            this.setState(state => ({
                sortedItems: Table.sortItems(this.props, state.sortBy, state.sortDirection),
            }));
        }
    }

    rowGetter = ({ index }: Index) => this.state.sortedItems[index];

    rowStyle = ({ index }: Index) => {
        const { rowStyle } = this.props;
        if (typeof rowStyle === 'function') {
            const item = this.state.sortedItems[index];
            return rowStyle({ index, item });
        }
        return rowStyle;
    }

    render() {
        const {
            headerHeight,
            overscanRowCount,
            rowHeight,
            scrollToIndex,
            sortBy,
            sortDirection,
            sortedItems,
        } = this.state;

        const { noRowsRenderer } = this.props;

        return (
            <AutoSizer>
                { ({ height, width }: Dimensions) =>
                    <VTable
                        disableHeader={ false }
                        headerClassName={ 'headerColumn' }
                        headerHeight={ headerHeight }
                        height={ height }
                        noRowsRenderer={ noRowsRenderer }
                        onRowClick={ this.onRowClick }
                        onRowDoubleClick={ this.onRowDoubleClick }
                        overscanRowCount={ overscanRowCount }
                        rowClassName={ this.rowClassName }
                        rowHeight={ rowHeight }
                        rowGetter={ this.rowGetter }
                        rowCount={ sortedItems.length }
                        rowStyle={ this.rowStyle }
                        scrollToIndex={ scrollToIndex }
                        sort={ this.sort }
                        sortBy={ sortBy! }
                        sortDirection={ sortDirection }
                        width={ width }
                    >
                        { this.props.children }
                    </VTable>
                }
            </AutoSizer>
        );
    }

    /* KILL? KEEP?
    onScrollToRowChange = (event: any) => {
        const { rowCount }: any = this.state;
        let scrollToIndex: any = Math.min(
            rowCount - 1,
            parseInt(event.target.value, 10)
        );

        if (isNaN(scrollToIndex)) {
            scrollToIndex = undefined;
        }

        this.setState({ scrollToIndex });
    }
    */

    rowClassName = ({ index }: Index) => {
        if (index < 0) {
            return 'headerRow';
        } else {
            return index % 2 === 0 ? 'evenRow' : 'oddRow';
        }
    }

    sort = ({ sortBy, sortDirection }: { sortBy: string; sortDirection: SortDirectionType }) => {
        this.setState(state => ({
            sortBy,
            sortDirection,
            sortedItems: Table.sortItems<T>(this.props, sortBy, sortDirection),
        }));
    }
}



// WEBPACK FOOTER //
// ./src/ui/shared/Table.tsx